// src/services/groupCreate.js — create a group, and the privacy-invite flow.
//
// Ported from the app's NewGroupModal + ChatModal.handleAccept/DeclineGroupInvite,
// field for field, because both sides read the same documents.
//
// ── THE PRIVACY RULE ────────────────────────────────────────────────────────
// users/{uid}.privacySettings.groupAdd decides who may add that person to a
// group without asking:
//   'everyone'    → straight in
//   'my_contacts' → only if I am in THEIR userConnections
//   'except'      → everyone except the uids in groupAddExcept
// Anyone who fails lands in `pendingParticipants` instead of `participants`,
// and gets a private `group_invite` message in the 1:1 chat with Accept /
// Decline. Accepting moves them across and posts "joined via invite" in the
// group; declining just drops them from pending.

import {
  doc, collection, addDoc, setDoc, getDoc,
  increment, serverTimestamp,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from '../config/firebase';
import { getSocket, BACKEND_URL } from './socket';

/**
 * May I add this person straight into a group?
 *
 * 🟥 FAILS OPEN on a read error — deliberately, because the APP fails open here
 *    (NewGroupModal.checkCanAddUser returns true in its catch). Diverging would
 *    mean the same person is addable from one client and not the other. If you
 *    ever want this to fail closed, change it in both places at once.
 */
export async function canAddToGroup(targetUid, myUid) {
  try {
    const snap = await getDoc(doc(db, 'users', targetUid));
    if (!snap.exists()) return true;
    const privacy = snap.data()?.privacySettings || {};
    const setting = privacy.groupAdd || 'everyone';
    if (setting === 'everyone') return true;
    if (setting === 'my_contacts') {
      const conn = await getDoc(doc(db, 'userConnections', targetUid));
      if (!conn.exists()) return false;
      return (conn.data()?.connections || []).some((c) => c?.uid === myUid || c?.id === myUid);
    }
    if (setting === 'except') return !((privacy.groupAddExcept || []).includes(myUid));
    return true;
  } catch {
    return true;
  }
}

/** The private "X invited you to join …" card, in the 1:1 chat. */
async function sendPrivateInvite({ me, target, groupId, groupName, groupImage }) {
  const chatId = [me.uid, target.uid].sort().join('_');
  const msgRef = await addDoc(collection(db, 'chats', chatId, 'messages'), {
    type: 'system',
    systemType: 'group_invite',
    sysKind: 'group_created',
    sysParams: { name: me.name, groupName },
    text: `${me.name} invited you to join "${groupName}"`,
    groupName,
    groupId,
    groupImage: groupImage || null,
    status: 'pending',
    senderId: me.uid,
    senderName: me.name,
    timestamp: serverTimestamp(),
    sent: true,
    delivered: false,
    seen: false,
  });

  const s = getSocket();
  if (s) {
    try {
      s.emit('send-group-invite', {
        toUserId: target.uid,
        groupName,
        inviterName: me.name,
        chatId,
        groupId,
        inviteId: msgRef.id,
        groupImage: groupImage || '',
        inviterImage: me.image || '',
        participantImages: JSON.stringify([me.image || '']),
      });
    } catch {}
  }

  await setDoc(doc(db, 'chats', chatId), {
    participants: [me.uid, target.uid],
    lastMessage: `✉️ Group Invitation from ${me.name}`,
    lastMessageSystem: {
      type: 'group_invite',
      senderName: me.name,
      groupName,
      sysKind: 'group_created',
      sysParams: { name: me.name, groupName },
    },
    lastMessageSenderId: me.uid,
    timestamp: serverTimestamp(),
    unreadCounts: { [target.uid]: increment(1) },
  }, { merge: true });
}

/**
 * Create the group. `members` are user objects ({uid, fullName, username,
 * profileImage}); the caller has already pre-selected whoever it wants.
 * Returns { groupId, invited: [names] }.
 */
export async function createGroup({ me, name, description = '', imageFile = null, members: rawMembers = [] }) {
  const groupName = String(name || '').trim();
  if (!groupName) throw new Error('name required');
  /* 🟥 THE LAST GATE BEFORE THE WRITE. The pickers already filter guests out, but
     a picker is a UI and this is the function that actually creates the document.
     A guest's access is one link with an expiry on it; a group would outlive both
     and put a stranger in a room with people who never opted into them. So the
     refusal lives here too, where every caller passes through. */
  const members = rawMembers.filter((u) => u && !(u.isGuest === true || u.guestLinkToken));
  if (members.length !== rawMembers.length) throw new Error('guest_not_allowed');
  if (!members.length) throw new Error('members required');

  // the app's own id shape, so nothing downstream has to special-case ours
  const groupId = `group_${me.uid}_${Date.now()}`;

  let groupImage = '';
  if (imageFile) {
    const r = storageRef(storage, `groupImages/${me.uid}/${Date.now()}.jpg`);
    await uploadBytes(r, imageFile, { contentType: imageFile.type || 'image/jpeg' });
    groupImage = await getDownloadURL(r);
  }

  // split by each person's groupAdd privacy
  const active = [me.uid];
  const pending = [];
  const invitedNames = [];
  for (const u of members) {
    // eslint-disable-next-line no-await-in-loop
    const ok = await canAddToGroup(u.uid, me.uid);
    if (ok) active.push(u.uid);
    else { pending.push(u.uid); invitedNames.push(u.fullName || u.username || 'Someone'); }
  }

  const participantNames = { [me.uid]: me.name };
  const participantImages = { [me.uid]: me.image || '' };
  for (const u of members) {
    participantNames[u.uid] = u.fullName || u.username || 'Unknown User';
    // the app refetches a missing image so the collage isn't stuck on a placeholder
    let img = u.profileImage;
    if (!img) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const s = await getDoc(doc(db, 'users', u.uid));
        img = s.data()?.profileImage || '';
      } catch { img = ''; }
    }
    participantImages[u.uid] = img || '';
  }

  await setDoc(doc(db, 'chats', groupId), {
    id: groupId,
    name: groupName,
    description: String(description || '').trim(),
    createdBy: me.uid,
    participants: active,
    pendingParticipants: pending,
    participantNames,
    participantImages,
    groupImage,
    isGroup: true,
    createdAt: serverTimestamp(),
    permissions: {
      editSettings: true, sendMessages: true, addMembers: true, approveMembers: false,
    },
    lastMessage: 'Group created',
    lastMessageSystem: {
      type: 'group_created',
      name: me.name,
      groupName,
      sysKind: 'group_created',
      sysParams: { name: me.name, groupName },
    },
    timestamp: serverTimestamp(),
    unreadCounts: active.reduce((a, u) => { a[u] = u === me.uid ? 0 : 1; return a; }, {}),
    admins: [me.uid],
  });

  const memberNames = members.map((u) => u.fullName || u.username || 'User');
  const welcome = await addDoc(collection(db, 'chats', groupId, 'messages'), {
    type: 'system',
    systemType: 'group_created',
    sysKind: 'group_created',
    sysParams: { name: me.name, groupName },
    text: `${me.name} created the group "${groupName}" with ${memberNames.join(', ')}`,
    senderName: me.name,
    groupName,
    initialMembers: memberNames,
    senderId: me.uid,
    timestamp: serverTimestamp(),
    sent: true, delivered: false, seen: false,
  });

  // live relay + push for everyone who went straight in
  const s = getSocket();
  if (s) {
    for (const uid of active) {
      if (uid === me.uid) continue;
      try {
        s.emit('new-message', {
          toUserId: uid,
          fromUserId: me.uid,
          message: `${me.name} added you to "${groupName}"`,
          chatId: groupId,
          messageId: welcome.id,
          systemType: 'group_created',
          sysKind: 'added_to_group',
          sysParams: JSON.stringify({ name: me.name, groupName }),
          isGroup: true,
          senderName: me.name,
          groupName,
          initialMembers: memberNames,
        });
      } catch {}
    }
  }

  // and a private invite for everyone whose privacy said "ask first"
  await Promise.all(members.filter((u) => pending.includes(u.uid))
    .map((u) => sendPrivateInvite({ me, target: u, groupId, groupName, groupImage })));

  return { groupId, invited: invitedNames };
}

/* ── the invite card's two buttons ─────────────────────────────────────────── */

/* 👥 THROUGH THE SERVER (POST /api/group/invite-respond). 🟥 One batch updated the invite card — a
      message the INVITER sent, which only its sender may change — together with the group, so the rules
      refused the whole batch: accepting never added you, declining never took you off the list.
      🟢 The server does the move as one transaction, on your verified identity, while the invite stands. */
async function respondToInvite(body) {
  const idToken = await auth?.currentUser?.getIdToken().catch(() => null);
  const r = await fetch(`${BACKEND_URL}/api/group/invite-respond`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...body, ...(idToken ? { idToken } : {}) }),
  });
  let j = null;
  try { j = await r.json(); } catch { j = null; }
  if (!r.ok || !j || !j.ok) { const e = new Error((j && j.error) || `http_${r.status}`); e.code = (j && j.error) || 'server'; throw e; }
  return j;
}

export async function acceptGroupInvite({ chatId, messageId, groupId, me }) {
  const text = `${me.name} joined via invite`;
  const j = await respondToInvite({ groupId, inviteChatId: chatId, inviteMessageId: messageId, accept: true, systemText: text });
  const s = getSocket();
  if (s && j.messageId) {
    for (const uid of j.members || []) {
      try {
        s.emit('new-message', {
          toUserId: uid, fromUserId: me.uid, message: text,
          chatId: groupId, messageId: j.messageId, isGroup: true,
          systemType: 'user_joined', sysKind: 'joined_invite',
          sysParams: JSON.stringify({ name: me.name }), senderName: me.name,
        });
      } catch {}
    }
  }
}

export async function declineGroupInvite({ chatId, messageId, groupId }) {
  await respondToInvite({ groupId, inviteChatId: chatId, inviteMessageId: messageId, accept: false });
}

/** The card's current state, straight off the message doc. */
export const inviteStatusOf = (m) => String(m?.status || 'pending');
